import { useMemo } from "react";
import { Badge, Flex, Text } from "@radix-ui/themes";
import {
  CheckCircledIcon,
  CrossCircledIcon,
  Half2Icon,
  QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import {
  ReleaseAccessStatus,
  ReleaseAccessResourceTopic,
  accessStatusToLabel,
  STAGED_TOOLTIP_TEXT,
} from "@/v2/entities/ReleaseResourceAccess/ReleaseResourceAccess.model";
import { Tooltip } from "@/components/shared/tooltip";

interface TopicInfo {
  id: number;
  topic: string;
}

interface AccessBadgeProps {
  status: ReleaseAccessStatus;
  isStaged?: boolean;
  topicsAccess?: ReleaseAccessResourceTopic[];
  publishedTopicsAccess?: ReleaseAccessResourceTopic[];
  topics?: TopicInfo[];
}

type BadgeConfig = { color: "green" | "gray" | "orange" | "blue"; icon: React.ReactNode };

const accessStatusConfig: Record<ReleaseAccessStatus, BadgeConfig> = {
  FULL_ACCESS: {
    color: "green",
    icon: <CheckCircledIcon width={12} height={12} />,
  },
  NO_ACCESS: {
    color: "gray",
    icon: <CrossCircledIcon width={12} height={12} />,
  },
  LIMITED_ACCESS: {
    color: "orange",
    icon: <Half2Icon width={12} height={12} />,
  },
};

const fallbackConfig: BadgeConfig = {
  color: "orange",
  icon: <QuestionMarkCircledIcon width={12} height={12} />,
};

interface TopicWithStaged extends TopicInfo {
  isStaged: boolean;
}

export function AccessBadge({
  status,
  isStaged = false,
  topicsAccess = [],
  publishedTopicsAccess = [],
  topics = [],
}: AccessBadgeProps) {
  const config = accessStatusConfig[status] ?? fallbackConfig;
  const baseLabel = accessStatusToLabel[status] ?? status;
  const label = isStaged ? `${baseLabel} (staged)` : baseLabel;
  const badgeColor = isStaged ? "blue" : config.color;

  const fullAccessTopics = useMemo((): TopicWithStaged[] => {
    if (status !== "LIMITED_ACCESS" || topics.length === 0) return [];

    const currentFullAccessTopicIds = new Set(
      topicsAccess
        .filter((ta) => ta.status === "FULL_ACCESS")
        .map((ta) => ta.topicId)
    );

    const publishedFullAccessTopicIds = new Set(
      publishedTopicsAccess
        .filter((ta) => ta.status === "FULL_ACCESS")
        .map((ta) => ta.topicId)
    );

    return topics
      .filter((topic) => currentFullAccessTopicIds.has(String(topic.id)))
      .map((topic) => ({
        ...topic,
        isStaged: !publishedFullAccessTopicIds.has(String(topic.id)),
      }))
      .sort((a, b) => a.topic.localeCompare(b.topic));
  }, [status, topicsAccess, publishedTopicsAccess, topics]);

  const badge = (
    <Badge color={badgeColor} size="1" variant="soft">
      <Text size="1" className="flex items-center gap-1">
        {config.icon}
        {label}
      </Text>
    </Badge>
  );

  if (isStaged) {
    return (
      <Tooltip>
        <Tooltip.Trigger>
          <span>{badge}</span>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Text size="1">{STAGED_TOOLTIP_TEXT}</Text>
        </Tooltip.Content>
      </Tooltip>
    );
  }

  if (status === "LIMITED_ACCESS" && fullAccessTopics.length > 0) {
    return (
      <Tooltip>
        <Tooltip.Trigger>
          <span>{badge}</span>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Flex gap="1" wrap="wrap" style={{ maxWidth: 300 }}>
            {fullAccessTopics.map((topic) => (
              <Badge
                key={topic.id}
                color={topic.isStaged ? "blue" : "green"}
                size="1"
                variant="soft"
              >
                <Text size="1">
                  {topic.topic}{topic.isStaged ? " (staged)" : ""}
                </Text>
              </Badge>
            ))}
          </Flex>
        </Tooltip.Content>
      </Tooltip>
    );
  }

  return badge;
}
